import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui';
import { buildWhatsAppLink, defaultMessage, quickMessages } from '../../data/contact';

const open = (text) => window.open(buildWhatsAppLink(text), '_blank', 'noopener,noreferrer');

export default function WhatsAppWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(defaultMessage);
  const pillRef = useRef(null);
  const panelRef = useRef(null);

  // Escape closes and returns focus to the pill; focus moves into the panel on open.
  useEffect(() => {
    if (!isOpen) return undefined;
    panelRef.current?.querySelector('button')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        pillRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const send = (text) => {
    open(text);
    setIsOpen(false);
  };
  const empty = message.trim() === '';

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-3 pb-[env(safe-area-inset-bottom)] pr-[env(safe-area-inset-right)]">
      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-labelledby="wa-title"
          className="w-[min(20rem,calc(100vw-2rem))] rounded-frame bg-paper-2 p-4 shadow-none ring-1 ring-rule"
        >
          <h2 id="wa-title" className="font-display text-h3">
            Message me on WhatsApp
          </h2>
          <ul className="mt-3 space-y-1">
            {quickMessages.map((text) => (
              <li key={text}>
                <button
                  type="button"
                  onClick={() => send(text)}
                  className="min-h-11 w-full px-2 text-left font-body text-body underline decoration-1 underline-offset-4 transition-colors duration-fast hover:bg-mark hover:text-on-mark focus-visible:bg-mark focus-visible:text-on-mark"
                >
                  {text}
                </button>
              </li>
            ))}
          </ul>
          <label htmlFor="wa-message" className="mt-4 block font-mono text-note text-ink-2">
            Your message
          </label>
          <textarea
            id="wa-message"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="mt-1 w-full rounded-frame border border-rule bg-paper p-2 font-body text-body"
          />
          <p className="mt-1 min-h-5 font-mono text-note text-ink-2" aria-live="polite">
            {empty ? 'Write a message first.' : ''}
          </p>
          <Button onClick={() => send(message)} disabled={empty} className="mt-2 w-full">
            Open WhatsApp
          </Button>
        </div>
      )}
      <button
        ref={pillRef}
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className="min-h-11 rounded-full bg-ink px-5 font-display font-semibold text-paper transition-colors duration-fast hover:bg-paper hover:text-ink hover:ring-2 hover:ring-inset hover:ring-ink focus-visible:bg-paper focus-visible:text-ink focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink"
      >
        {isOpen ? 'Close' : 'Message me'}
      </button>
    </div>
  );
}
